import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import { Color } from "src/elements/Color/Color";
import {
  getTraversalFlattenedItems,
  hasCompleteSnakingPattern,
  hasUnsupportedFixtures,
  SnakingPattern,
} from "../../../../../common/snakingInference";

export type CdtPriorityLevel = "cdt0" | "cdt1" | "cdt2" | "cdt3";

export type PrioritizedCdtPriorityLevel = Exclude<CdtPriorityLevel, "cdt0">;

export type CdtPriorityField = "cdt1Priority" | "cdt2Priority" | "cdt3Priority";

export type CdtPrioritySource = "ranged" | "unranged";

export interface CdtDuplicatePath {
  path: readonly [string, string, string, string];
  productCodes: string[];
}

export interface CdtPriorityItem {
  uniqueId: string;
  productCode: string;
  name: string;
  variant: string;
  cdt0: string;
  cdt1: string;
  cdt1Priority: number;
  cdt2: string;
  cdt2Priority: number;
  cdt3: string;
  cdt3Priority: number;
  source: CdtPrioritySource;
  placement:
    | {
        kind: "shelf";
        bayNo: number;
        shelfNo: number;
      }
    | {
        kind: "pegboard";
        bayNo: number;
        pegboardId: string;
        holeNoLeft: number;
        holeNoTop: number;
      }
    | null;
  originalOrder: number;
}

interface CdtGroupValues {
  variant: string;
  cdt1: string;
  cdt2: string;
  cdt3: string;
}

export interface CdtPriorityProductNode {
  type: "product";
  id: string;
  item: CdtPriorityItem;
}

export interface CdtPriorityGroupNode {
  type: "group";
  id: string;
  parentId: string | null;
  level: CdtPriorityLevel;
  name: string;
  priority: number | null;
  isMixedPriority: boolean;
  itemIds: string[];
  productCount: number;
  hasRangedItems: boolean;
  hasUnrangedItems: boolean;
  children: CdtPriorityTreeNode[];
}

export type CdtPriorityTreeNode = CdtPriorityGroupNode | CdtPriorityProductNode;

export interface CdtPriorityReorderGroup {
  id: string;
  itemIds: string[];
}

export type CdtPriorityRecalculationFailure =
  | "unsupportedFixtures"
  | "incompleteSnakingPattern"
  | "invalidShelfTraversal";

export type CdtPriorityRecalculationResult =
  | { success: true; pog: Pog }
  | { success: false; reason: CdtPriorityRecalculationFailure };

export const NO_CDT_GROUP_NAME = "(No CDT)";

const CDT_GROUP_PALETTE = [
  Color.primary,
  Color.infoBlue,
  Color.paletteGold,
  Color.purple,
  Color.palettePink,
  Color.paletteTealDark,
  Color.paletteOrange,
  Color.paletteIndigo,
  Color.paletteSkyBlue,
  Color.paletteRed,
  Color.paletteEmerald,
  Color.palettePinkLight,
  Color.paletteTeal,
  Color.paletteDarkSlate,
  Color.infoBlueDarker,
  Color.alertCriticalIcon,
];

export const getCdtGroupColor = (groupId: string): string => {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash << 5) - hash + groupId.charCodeAt(i);
    hash |= 0;
  }
  const positiveHash = Math.abs(hash);
  return CDT_GROUP_PALETTE[positiveHash % CDT_GROUP_PALETTE.length];
};

export const createCdtGroupColorMap = (groupIds: Iterable<string>) => {
  const uniqueGroupIds = [...new Set(groupIds)].sort();

  return new Map(
    uniqueGroupIds.map((groupId, index) => [
      groupId,
      CDT_GROUP_PALETTE[index % CDT_GROUP_PALETTE.length],
    ]),
  );
};

const cdtPriorityFields = {
  cdt1: "cdt1Priority",
  cdt2: "cdt2Priority",
  cdt3: "cdt3Priority",
} satisfies Record<PrioritizedCdtPriorityLevel, CdtPriorityField>;

const cdtLevelIndexes = {
  cdt0: 0,
  cdt1: 1,
  cdt2: 2,
  cdt3: 3,
} satisfies Record<CdtPriorityLevel, number>;

const normalizeSearch = (search: string | undefined) => (search || "").trim().toLowerCase();

const getCdtGroupPath = (item: CdtGroupValues, level: CdtPriorityLevel) => {
  const path = [item.variant, item.cdt1, item.cdt2, item.cdt3];
  return path.slice(0, cdtLevelIndexes[level] + 1);
};

const getCdtGroupId = (level: CdtPriorityLevel, path: string[]) =>
  `${level}:${path.map(encodeURIComponent).join("|")}`;

export const getCdtGroupIdForItem = (item: CdtGroupValues, level: CdtPriorityLevel) =>
  getCdtGroupId(level, getCdtGroupPath(item, level));

export const getCdtGroupIdsForPog = (
  pog: Pog,
  items: CdtPriorityItem[],
  level: CdtPriorityLevel,
) => {
  const groupIds = [
    ...items.map((item) => getCdtGroupIdForItem(item, level)),
    ...pog.planogram.bays.flatMap((bay) =>
      bay.pegboards.flatMap((pegboard) =>
        pegboard.placements.map((placement) => getCdtGroupIdForItem(placement.pegProduct, level)),
      ),
    ),
  ];

  return [...new Set(groupIds)];
};

const getGroupName = (value: string) => value || NO_CDT_GROUP_NAME;

const isPrioritizedCdtPriorityLevel = (
  level: CdtPriorityLevel,
): level is PrioritizedCdtPriorityLevel => level !== "cdt0";

const getCdtPriorityField = (level: CdtPriorityLevel): CdtPriorityField | null =>
  isPrioritizedCdtPriorityLevel(level) ? cdtPriorityFields[level] : null;

interface PegboardPlacementMetadata {
  kind: "pegboard";
  bayNo: number;
  pegboardId: string;
  holeNoLeft: number;
  holeNoTop: number;
}

const getPegboardPlacementMetadata = (pog: Pog) => {
  const metadata = new Map<string, PegboardPlacementMetadata>();

  for (const bay of pog.planogram.bays) {
    for (const pegboard of bay.pegboards) {
      for (const placement of pegboard.placements) {
        const anchor = placement.holes[0];
        if (!anchor) continue;
        metadata.set(placement.pegProduct.uniqueId, {
          kind: "pegboard",
          bayNo: bay.getNo(),
          pegboardId: pegboard.uniqueId,
          holeNoLeft: anchor.holeNoLeft,
          holeNoTop: anchor.holeNoTop,
        });
      }
    }
  }

  return metadata;
};

const getRangedProductsInDefaultOrder = (pog: Pog): BaseProduct[] =>
  pog.planogram.bays.flatMap((bay) => [
    ...bay.shelves.flatMap((shelf) => shelf.items),
    ...bay.pegboards.flatMap((pegboard) =>
      pegboard.placements.map((placement) => placement.pegProduct),
    ),
  ]);

export const collectCdtPriorityItems = (pog: Pog, pattern?: SnakingPattern): CdtPriorityItem[] => {
  const items: CdtPriorityItem[] = [];
  const rangedProductKeys = new Set<string>();
  const pegboardPlacementMetadata = getPegboardPlacementMetadata(pog);
  let originalOrder = 0;
  const sourceItems = pattern
    ? getTraversalFlattenedItems<BaseProduct>(pog, pattern)
    : getRangedProductsInDefaultOrder(pog);

  for (const item of sourceItems) {
    rangedProductKeys.add(item.productCode);
    const placement = pegboardPlacementMetadata.get(item.uniqueId);
    const shelf = item instanceof PlanogramItem ? item.shelf : null;
    const shelfPlacement: Extract<CdtPriorityItem["placement"], { kind: "shelf" }> | null = shelf
      ? {
          kind: "shelf",
          bayNo: shelf.bay.getNo(),
          shelfNo: shelf.getNo(),
        }
      : null;

    items.push({
      uniqueId: item.uniqueId,
      productCode: item.productCode,
      name: item.name,
      variant: item.variant,
      cdt0: item.variant,
      cdt1: item.cdt1,
      cdt1Priority: item.cdt1Priority,
      cdt2: item.cdt2,
      cdt2Priority: item.cdt2Priority,
      cdt3: item.cdt3,
      cdt3Priority: item.cdt3Priority,
      source: "ranged",
      placement: shelfPlacement ?? placement ?? null,
      originalOrder,
    });
    originalOrder++;
  }

  for (const item of pog.unrangedItems) {
    if (rangedProductKeys.has(item.productCode)) continue;

    items.push({
      uniqueId: item.uniqueId,
      productCode: item.productCode,
      name: item.name,
      variant: item.variant,
      cdt0: item.variant,
      cdt1: item.cdt1,
      cdt1Priority: item.cdt1Priority,
      cdt2: item.cdt2,
      cdt2Priority: item.cdt2Priority,
      cdt3: item.cdt3,
      cdt3Priority: item.cdt3Priority,
      source: "unranged",
      placement: null,
      originalOrder,
    });
    originalOrder++;
  }

  return items;
};

const comparePriorityItemsByOriginalOrder = (itemA: CdtPriorityItem, itemB: CdtPriorityItem) =>
  itemA.originalOrder - itemB.originalOrder;

const getVisibleProductItems = (nodes: CdtPriorityTreeNode[]): CdtPriorityItem[] => {
  const items: CdtPriorityItem[] = [];

  for (const node of nodes) {
    if (node.type === "product") {
      items.push(node.item);
      continue;
    }

    items.push(...getVisibleProductItems(node.children));
  }

  return items;
};

const updateGroupWithVisibleChildren = (
  node: CdtPriorityGroupNode,
  children: CdtPriorityTreeNode[],
): CdtPriorityGroupNode => {
  const visibleItems = getVisibleProductItems(children);
  const priorityField = getCdtPriorityField(node.level);
  const priorities = priorityField
    ? Array.from(new Set(visibleItems.map((item) => item[priorityField])))
    : [];
  const sortedPriorities = [...priorities].sort((priorityA, priorityB) => priorityA - priorityB);

  return {
    ...node,
    children,
    itemIds: visibleItems.map((item) => item.uniqueId),
    productCount: visibleItems.length,
    hasRangedItems: visibleItems.some((item) => item.source === "ranged"),
    hasUnrangedItems: visibleItems.some((item) => item.source === "unranged"),
    priority: sortedPriorities[0] ?? null,
    isMixedPriority: priorityField ? priorities.length > 1 : false,
  };
};

const buildGroups = (
  items: CdtPriorityItem[],
  levelIndex: number,
  parentId: string | null,
  includeChildren: boolean,
): CdtPriorityGroupNode[] => {
  const levels: CdtPriorityLevel[] = ["cdt0", "cdt1", "cdt2", "cdt3"];
  const level = levels[levelIndex];
  const priorityField = getCdtPriorityField(level);
  const groups = new Map<string, { path: string[]; items: CdtPriorityItem[] }>();

  for (const item of items) {
    const path = getCdtGroupPath(item, level);
    const groupId = getCdtGroupId(level, path);
    const group = groups.get(groupId) || { path, items: [] };
    group.items.push(item);
    groups.set(groupId, group);
  }

  return Array.from(groups.entries())
    .map(([, group]) => {
      const groupPath = group.path;
      const groupItems = group.items;
      const groupValue = groupPath[groupPath.length - 1];
      const priorities = priorityField
        ? Array.from(new Set(groupItems.map((item) => item[priorityField])))
        : [];
      const sortedPriorities = [...priorities].sort(
        (priorityA, priorityB) => priorityA - priorityB,
      );
      const groupId = getCdtGroupId(level, groupPath);
      const isLeafGroup = levelIndex === levels.length - 1;
      const minOriginalOrder = groupItems.reduce(
        (minOrder, item) => Math.min(minOrder, item.originalOrder),
        Number.POSITIVE_INFINITY,
      );
      const sortedItems = [...groupItems].sort(comparePriorityItemsByOriginalOrder);
      const children: CdtPriorityTreeNode[] = includeChildren
        ? isLeafGroup
          ? sortedItems.map(
              (item): CdtPriorityProductNode => ({
                type: "product",
                id: item.uniqueId,
                item,
              }),
            )
          : buildGroups(groupItems, levelIndex + 1, groupId, includeChildren)
        : [];
      const node: CdtPriorityGroupNode = {
        type: "group",
        id: groupId,
        parentId,
        level,
        name: getGroupName(groupValue),
        priority: sortedPriorities[0] ?? null,
        isMixedPriority: priorityField ? priorities.length > 1 : false,
        itemIds: groupItems.map((item) => item.uniqueId),
        productCount: groupItems.length,
        hasRangedItems: groupItems.some((item) => item.source === "ranged"),
        hasUnrangedItems: groupItems.some((item) => item.source === "unranged"),
        children,
      };

      return { node, minOriginalOrder };
    })
    .sort((groupA, groupB) => {
      const minOrderA = groupA.minOriginalOrder;
      const minOrderB = groupB.minOriginalOrder;

      if (groupA.node.priority === null && groupB.node.priority === null) {
        return minOrderA - minOrderB;
      }

      if (groupA.node.priority === null) {
        return 1;
      }

      if (groupB.node.priority === null) {
        return -1;
      }

      if (groupA.node.priority !== groupB.node.priority) {
        return groupA.node.priority - groupB.node.priority;
      }

      const nameCompare = groupA.node.name.localeCompare(groupB.node.name);
      if (nameCompare !== 0) {
        return nameCompare;
      }

      return minOrderA - minOrderB;
    })
    .map(({ node }) => node);
};

const itemMatchesSearch = (item: CdtPriorityItem, search: string) => {
  return (
    item.name.toLowerCase().includes(search) ||
    item.productCode.toLowerCase().includes(search) ||
    item.cdt0.toLowerCase().includes(search) ||
    item.cdt1.toLowerCase().includes(search) ||
    item.cdt2.toLowerCase().includes(search) ||
    item.cdt3.toLowerCase().includes(search)
  );
};

const applySearch = (nodes: CdtPriorityTreeNode[], search: string): CdtPriorityTreeNode[] => {
  if (!search) {
    return nodes;
  }

  const matchingNodes: CdtPriorityTreeNode[] = [];

  for (const node of nodes) {
    if (node.type === "product") {
      if (itemMatchesSearch(node.item, search)) {
        matchingNodes.push(node);
      }
      continue;
    }

    if (node.name.toLowerCase().includes(search)) {
      matchingNodes.push(node);
      continue;
    }

    const children = applySearch(node.children, search);
    if (children.length === 0) {
      continue;
    }

    matchingNodes.push(updateGroupWithVisibleChildren(node, children));
  }

  return matchingNodes;
};

export const buildCdtPriorityTree = (
  items: CdtPriorityItem[],
  selectedLevel: CdtPriorityLevel,
  {
    search,
    includeChildren = true,
  }: {
    search?: string;
    includeChildren?: boolean;
  } = {},
): CdtPriorityGroupNode[] => {
  const tree = buildGroups(items, cdtLevelIndexes[selectedLevel], null, includeChildren);

  return applySearch(tree, normalizeSearch(search)).filter(
    (node): node is CdtPriorityGroupNode => node.type === "group",
  );
};

export const getSiblingCdtPriorityGroups = (
  nodes: CdtPriorityTreeNode[],
  parentId: string | null,
  level: CdtPriorityLevel,
): CdtPriorityGroupNode[] => {
  if (parentId === null) {
    return nodes.filter(
      (node): node is CdtPriorityGroupNode => node.type === "group" && node.level === level,
    );
  }

  for (const node of nodes) {
    if (node.type !== "group") {
      continue;
    }

    if (node.id === parentId) {
      return node.children.filter(
        (child): child is CdtPriorityGroupNode => child.type === "group" && child.level === level,
      );
    }

    const childGroups = getSiblingCdtPriorityGroups(node.children, parentId, level);
    if (childGroups.length > 0) {
      return childGroups;
    }
  }

  return [];
};

export const reorderCdtPriorityGroups = <Group extends CdtPriorityReorderGroup>(
  groups: Group[],
  draggedGroupId: string,
  targetGroupId: string,
  position: "before" | "after",
) => {
  if (draggedGroupId === targetGroupId) {
    return groups;
  }

  const draggedGroup = groups.find((group) => group.id === draggedGroupId);
  if (!draggedGroup) {
    return groups;
  }

  const groupsWithoutDragged = groups.filter((group) => group.id !== draggedGroupId);
  const targetIndex = groupsWithoutDragged.findIndex((group) => group.id === targetGroupId);
  if (targetIndex === -1) {
    return groups;
  }

  const insertIndex = targetIndex + (position === "after" ? 1 : 0);
  return [
    ...groupsWithoutDragged.slice(0, insertIndex),
    draggedGroup,
    ...groupsWithoutDragged.slice(insertIndex),
  ];
};

const applyReorderedGroupsToTree = (
  nodes: CdtPriorityTreeNode[],
  level: PrioritizedCdtPriorityLevel,
  orderIndexByItemId: Map<string, number>,
): CdtPriorityTreeNode[] => {
  const getOrderIndex = (node: CdtPriorityTreeNode) => {
    if (node.type !== "group" || node.level !== level) {
      return null;
    }

    let orderIndex: number | null = null;

    for (const itemId of node.itemIds) {
      const itemOrderIndex = orderIndexByItemId.get(itemId);

      if (itemOrderIndex === undefined) {
        return null;
      }

      if (orderIndex === null) {
        orderIndex = itemOrderIndex;
        continue;
      }

      if (orderIndex !== itemOrderIndex) {
        return null;
      }
    }

    return orderIndex;
  };

  return nodes
    .map((node) => {
      if (node.type === "product") {
        return node;
      }

      return {
        ...node,
        children: applyReorderedGroupsToTree(node.children, level, orderIndexByItemId),
      };
    })
    .map((node, originalIndex) => ({
      node,
      originalIndex,
      orderIndex: getOrderIndex(node),
    }))
    .sort((nodeA, nodeB) => {
      if (
        nodeA.orderIndex !== null &&
        nodeB.orderIndex !== null &&
        nodeA.orderIndex !== nodeB.orderIndex
      ) {
        return nodeA.orderIndex - nodeB.orderIndex;
      }

      return nodeA.originalIndex - nodeB.originalIndex;
    })
    .map(({ node }) => node);
};

const getOrderIndexByItemId = (orderedGroups: CdtPriorityReorderGroup[]) => {
  const orderIndexByItemId = new Map<string, number>();

  orderedGroups.forEach((group, groupIndex) => {
    for (const itemId of group.itemIds) {
      orderIndexByItemId.set(itemId, groupIndex);
    }
  });

  return orderIndexByItemId;
};

const normalizeCdtPathValue = (value: string | null | undefined) => value ?? "";

const getCdtPathKey = (...values: (string | null | undefined)[]) =>
  values.map(normalizeCdtPathValue).join("\x00");

export const getDuplicateCdtPaths = (pog: Pog): CdtDuplicatePath[] => {
  const paths = new Map<
    string,
    { path: [string, string, string, string]; productCodes: Set<string> }
  >();

  for (const item of collectCdtPriorityItems(pog)) {
    const path: [string, string, string, string] = [
      normalizeCdtPathValue(item.variant),
      normalizeCdtPathValue(item.cdt1),
      normalizeCdtPathValue(item.cdt2),
      normalizeCdtPathValue(item.cdt3),
    ];
    const key = getCdtPathKey(...path);
    const entry = paths.get(key) ?? { path, productCodes: new Set<string>() };
    entry.productCodes.add(item.productCode);
    paths.set(key, entry);
  }

  return [...paths.values()]
    .filter((entry) => entry.productCodes.size > 1)
    .map(({ path, productCodes }) => ({ path, productCodes: [...productCodes] }));
};

const hasSameItemIds = (left: BaseProduct[], right: BaseProduct[]) => {
  if (left.length !== right.length) return false;

  const leftIds = new Set(left.map((item) => item.uniqueId));
  const rightIds = new Set(right.map((item) => item.uniqueId));

  return (
    leftIds.size === left.length &&
    rightIds.size === right.length &&
    [...leftIds].every((itemId) => rightIds.has(itemId))
  );
};

export const recalculateCdtPriorities = (
  pog: Pog,
  pattern: SnakingPattern,
): CdtPriorityRecalculationResult => {
  let repairedPog: Pog;

  try {
    repairedPog = new Pog(pog.toNonCircular());
  } catch {
    return { success: false, reason: "invalidShelfTraversal" };
  }

  if (hasUnsupportedFixtures(repairedPog)) {
    return { success: false, reason: "unsupportedFixtures" };
  }

  if (!hasCompleteSnakingPattern<BaseProduct>(repairedPog, pattern)) {
    return { success: false, reason: "incompleteSnakingPattern" };
  }

  const physicalItems = repairedPog.planogram.getAllPlanogramProducts();
  const traversalItems = getTraversalFlattenedItems<BaseProduct>(repairedPog, pattern);

  if (!hasSameItemIds(physicalItems, traversalItems)) {
    return { success: false, reason: "invalidShelfTraversal" };
  }

  const rangedProductCodes = new Set(physicalItems.map((item) => item.productCode));
  const unrangedItems = repairedPog.unrangedItems.filter(
    (item) => !rangedProductCodes.has(item.productCode),
  );
  const priorityItems: BaseProduct[] = [...traversalItems, ...unrangedItems];

  const cdt1Priorities = new Map<string, number>();
  const cdt2Priorities = new Map<string, number>();

  priorityItems.forEach((item, index) => {
    item.cdt3Priority = index + 1;

    const cdt1Path = getCdtPathKey(item.variant, item.cdt1);
    const cdt2Path = getCdtPathKey(item.variant, item.cdt1, item.cdt2);
    cdt1Priorities.set(
      cdt1Path,
      Math.min(cdt1Priorities.get(cdt1Path) ?? Number.POSITIVE_INFINITY, item.cdt3Priority),
    );
    cdt2Priorities.set(
      cdt2Path,
      Math.min(cdt2Priorities.get(cdt2Path) ?? Number.POSITIVE_INFINITY, item.cdt3Priority),
    );
  });

  priorityItems.forEach((item) => {
    item.cdt1Priority = cdt1Priorities.get(getCdtPathKey(item.variant, item.cdt1)) ?? 0;
    item.cdt2Priority = cdt2Priorities.get(getCdtPathKey(item.variant, item.cdt1, item.cdt2)) ?? 0;
  });

  return { success: true, pog: repairedPog };
};

const assignContinuousCdtPriorities = (pog: Pog, nodes: CdtPriorityTreeNode[]) => {
  let priority = 1;
  const itemMap = new Map<string, BaseProduct>();

  for (const product of pog.planogram.getAllPlanogramProducts()) {
    itemMap.set(product.uniqueId, product);
  }

  const assignNode = (node: CdtPriorityTreeNode) => {
    if (node.type === "product") {
      return;
    }

    const priorityField = getCdtPriorityField(node.level);
    if (priorityField) {
      for (const itemId of node.itemIds) {
        const item = itemMap.get(itemId);
        if (item) {
          item[priorityField] = priority;
        }
      }

      priority++;
    }

    for (const child of node.children) {
      assignNode(child);
    }
  };

  for (const node of nodes) {
    assignNode(node);
  }
};

export const applyCdtPriorityGroupReorder = (
  pog: Pog,
  level: CdtPriorityLevel,
  orderedGroups: CdtPriorityReorderGroup[],
) => {
  const newPog = new Pog(pog.toNonCircular());
  if (!isPrioritizedCdtPriorityLevel(level)) {
    return newPog;
  }

  const cdtItems = collectCdtPriorityItems(newPog);
  const tree = buildCdtPriorityTree(cdtItems, "cdt0");
  const reorderedTree = applyReorderedGroupsToTree(
    tree,
    level,
    getOrderIndexByItemId(orderedGroups),
  );
  assignContinuousCdtPriorities(newPog, reorderedTree);

  return newPog;
};
